import type { SupervisorAuditedPrecision, SupervisorCheckPrecision, SupervisorFinding } from '@avisoma/shared';
import { formatValue } from '../../lib/formatValue';

export { formatValue };

/** Findings grouped by check id; groups keep the order in which each check first appears. */
export const groupByCheck = (items: SupervisorFinding[]): Array<{ checkId: string; items: SupervisorFinding[] }> => {
  const groups = new Map<string, SupervisorFinding[]>();
  for (const f of items) groups.set(f.checkId, [...(groups.get(f.checkId) ?? []), f]);
  return [...groups.entries()].map(([checkId, grouped]) => ({ checkId, items: grouped }));
};

/**
 * The page view for a page-scope finding, the reconstructed-document view for an entity finding,
 * else (a project-level finding with neither) the page that asserted it.
 */
export const targetOf = (f: SupervisorFinding): { href: string; label: 'page' | 'entity' } => {
  if (f.scope === 'page' && f.pageId) return { href: `/document-pages/${f.pageId}`, label: 'page' };
  if (f.reconstructedDocumentId) return { href: `/reconstructed-documents/${f.reconstructedDocumentId}`, label: 'entity' };
  return { href: `/document-pages/${f.assertingPageId}`, label: 'page' };
};

/**
 * Reason-column line for a stale finding: the value the write check found instead of the expected
 * one, or "Row no longer matches" when it found none (row missing or ambiguous, entity gone).
 */
export const staleObservedText = (f: SupervisorFinding): string | null => {
  if (f.status !== 'stale') return null;
  return f.observedValue === null ? 'Row no longer matches' : `Entity now holds: ${formatValue(f.observedValue)}`;
};

/** Spec "UI" tab header: manual applies and reverts since the last finalize (`pendingFinalizeCount`). */
export const pendingFinalizeLabel = (n: number): string =>
  `${n} ${n === 1 ? 'correction' : 'corrections'} applied, not finalized`;

/**
 * A rate as a whole percent, floored: a check with any false positive must never read "100 %". The epsilon keeps a
 * product that floating point puts just under a whole number on it (0.57 * 100 is 56.99999999999999, not 57).
 */
const floorPct = (rate: number | null): string => (rate === null ? 'n/a' : `${Math.floor(rate * 100 + 1e-9)} %`);

/**
 * "weak precision 92 %, 23 labels"; "weak precision n/a, 4 labels" while the server withholds it (under 10 labels).
 * "weak": the labels come from apply, revert and dismiss actions, not from an audit (phase 5).
 */
export const precisionLabel = (p: SupervisorCheckPrecision): string => {
  const labels = p.truePositives + p.falsePositives;
  const value = p.precision === null ? 'n/a' : floorPct(p.precision);
  return `weak precision ${value}, ${labels} ${labels === 1 ? 'label' : 'labels'}`;
};

export const precisionTitle = (p: SupervisorCheckPrecision): string => {
  const base = `${p.truePositives} true positives, ${p.falsePositives} false positives, ${p.unlabeled} unlabeled`;
  return p.autofixApplied > 0 || p.autofixReverted > 0
    ? `${base}; autofix applied ${p.autofixApplied}, reverted ${p.autofixReverted}`
    : base;
};

/** The newest version of each check: the one the run uses now. The route lists one item per `(checkId, checkVersion)`. */
export const latestPrecisionByCheck = (items: SupervisorCheckPrecision[]): Map<string, SupervisorCheckPrecision> =>
  items.reduce((map, p) => {
    const kept = map.get(p.checkId);
    return kept && kept.checkVersion >= p.checkVersion ? map : map.set(p.checkId, p);
  }, new Map<string, SupervisorCheckPrecision>());

/** "audited 25 · 76 %"; "audited 9" while the server withholds the rate (under 10 yes-or-no labels). Floored. */
export const auditedLabel = (a: SupervisorAuditedPrecision): string =>
  a.proposalPrecision === null ? `audited ${a.labels}` : `audited ${a.labels} · ${floorPct(a.proposalPrecision)}`;

/** Labels at or above which a low audited proposal precision raises the warning badge. */
const AUDIT_WARNING_MIN_LABELS = 20;
/** Audited proposal precision below this raises the warning badge. */
const AUDIT_WARNING_BELOW = 0.8;

/** A prompt for a human decision (retire, re-grain, or keep the check); nothing changes on its own. */
export const auditWarning = (a: SupervisorAuditedPrecision): boolean =>
  a.labels >= AUDIT_WARNING_MIN_LABELS && a.proposalPrecision !== null && a.proposalPrecision < AUDIT_WARNING_BELOW;

export const auditedTitle = (a: SupervisorAuditedPrecision): string =>
  `${a.labels} audit labels: real discrepancy ${floorPct(a.realDiscrepancyRate)}, proposed value correct ${floorPct(a.proposalPrecision)}, useful to a lessor ${floorPct(a.usefulRate)}`;
